<template>
  <div class="wrapper">
    <div
      class="item"
      @click="handleClick(1)"
      :data-testid="generateTestId('top', 'button', 'operation-status')"
    >
      <img alt="" :src="addAssetsPublicPath('static/images/common/header/icon_graph_02.svg')" />
      <div :class="['label', activeIndex === 1 ? 'active' : '']">運用状況</div>
    </div>
    <div
      class="item"
      @click="handleClick(2)"
      :data-testid="generateTestId('top', 'button', 'operation-history')"
    >
      <img alt="" :src="addAssetsPublicPath('static/images/common/header/icon_list_02.svg')" />
      <div :class="['label', activeIndex === 2 ? 'active' : '']">運用履歴</div>
    </div>
    <div
      class="item"
      @click="handleClick(3)"
      :data-testid="generateTestId('top', 'button', 'add-point')"
    >
      <img
        alt=""
        :src="addAssetsPublicPath('static/images/common/header/icon_assets_plus_4.svg')"
      />
      <div :class="['label']">運用する</div>
    </div>
    <div
      class="item"
      @click="handleClick(4)"
      :data-testid="generateTestId('top', 'button', 'other')"
    >
      <div v-if="hasUnreadNotices" class="badge"></div>
      <img
        alt=""
        :src="addAssetsPublicPath('static/images/common/header/icon_threepoint_02.svg')"
      />
      <div :class="['label', activeIndex === 4 ? 'active' : '']">その他</div>
    </div>
    <div
      v-show="isAccountOpened"
      class="item white"
      @click="onClickAssetManagement()"
      :data-testid="generateTestId('top', 'button', 'asset-management')"
    >
      <div class="balloon">{{ navBarIcon.balloonText }}</div>
      <div class="centered-content">
        <div
          v-if="navBarIcon.iconStyle === 'animation'"
          id="animated-icon"
          ref="animatedIcon"
        ></div>
        <img
          v-if="navBarIcon.iconStyle === 'image'"
          :src="addAssetsPublicPath('static/images/common/header/money_plus.svg')"
          alt=""
        />
        <div class="text">{{ navBarIcon.iconDescription }}</div>
        <!-- triangle centered relative to this area. -->
        <div class="balloon-triangle"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { addAssetsPublicPath } from '@/utils/env.js'
import { useCommonStore } from '@/store/common'
import { useTopStore } from '@/store/top'
import { f7 } from 'framework7-vue'
import { ref, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { sendGtag } from '@/utils/gtag'
import { generateTestId } from '@/utils/qa-test/testIds'

const props = defineProps<{
  hasUnreadNotices: boolean
  isAccountOpened: boolean
  activeIndex: number
}>()
const commonStore = useCommonStore()
const { setIsCourseSelectModalVisible } = commonStore
const { navBarIcon, userGroup, oprStatusGA } = storeToRefs(commonStore)

const topStore = useTopStore()
const { onClickAssetManagementLink } = topStore

const animatedIcon = ref(null)

const onClickAssetManagement = () => {
  // 01-01
  sendGtag({
    event: 'event',
    eventact: 'tap',
    eventcate: '上部アイコン',
    eventlab: 'ミニアプリ遷移',
    abtest: userGroup.value,
    COURSE: g.BANNER_GA_DATA && g.BANNER_GA_DATA.course,
    PRICE: g.BANNER_GA_DATA && g.BANNER_GA_DATA.price,
    userstatus: props.isAccountOpened ? '証券口座開設済み' : '証券口座未開設',
  })
  onClickAssetManagementLink('headerIcon')
}
const handleClick = (index: number): void => {
  if (index === props.activeIndex) return

  switch (index) {
    case 1:
    default:
      f7.tab.show('#view-home')
      break
    case 2:
      f7.tab.show('#view-log')
      break
    case 3:
      setIsCourseSelectModalVisible(true)
      sendGtag({
        event: 'event_btc',
        eventact: 'tap',
        eventcate: 'TOP_運用する',
        eventlab: '運用する',
        operational_status: oprStatusGA.value,
      })
      sendGtag({
        event: 'event_btc',
        eventact: 'impression',
        eventcate: '運用コースを選ぶ',
        operational_status: oprStatusGA.value,
      })
      break
    case 4:
      f7.tab.show('#view-etc')
      sendGtag({
        event: 'event_btc',
        eventact: 'tap',
        eventcate: 'TOP',
        operational_status: oprStatusGA.value,
      })
      sendGtag({
        event: 'event_btc',
        eventact: 'impression',
        eventcate: 'その他',
        operational_status: oprStatusGA.value,
      })
      break
  }
}

watch(
  navBarIcon,
  async (data) => {
    if (data.iconStyle === 'animation') {
      await nextTick()
      lottie.loadAnimation({
        container: animatedIcon.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: addAssetsPublicPath('static/animated-header-icon.json'),
      })
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@import 'src/css/mixins.scss';
.wrapper {
  display: flex;
  background: linear-gradient(180deg, #51a9dd, #5a8dcf);
  padding-bottom: 8px;

  .item {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    &.white {
      @include font-bold;
      background-color: #ffffff;
      border-radius: 4px;
      text-transform: none; // to avoid capitalization in Android
      margin-right: 8px;

      .balloon {
        font-size: 0.9rem;
        line-height: 1.4rem;
        color: #002970;
        background-color: #ffd98d;
        padding: 0px 6px;
        border-radius: 9999px;

        position: absolute;
        z-index: 1;
        top: -10px;
        width: max-content;
      }

      .text {
        font-size: 1.1rem;
        color: #002970;
      }

      .balloon-triangle {
        content: '';
        position: absolute;
        top: -4px;
        left: 50%;
        transform: translate(-50%, 0) rotate(45deg);
        border-bottom: solid 4px #ffd98d;
        border-right: solid 4px #ffd98d;
        border-bottom-right-radius: 2px;
        border-left: solid 4px transparent;
        border-top: solid 4px transparent;
      }

      .centered-content {
        display: flex;
        flex-direction: column;
        align-items: center;

        #animated-icon {
          height: 32px;
          width: 32px;
        }
      }
    }

    .badge {
      z-index: 2;
      width: 6px;
      height: 6px;
      min-width: auto;
      position: absolute;
      top: 4px;
      right: 20px;
      border-radius: 50%;
      background: #fd5c5c;
      padding: 0;
    }

    img {
      width: 33px;
    }
    .label {
      color: white;
      @include font-bold;
      font-size: 1.1rem;
      line-height: 1.6rem;
      padding: 2px 8px;
      &.active {
        background: rgba(0, 41, 112, 0.65);
        border-radius: 9999px;
      }
    }
  }
}
</style>
